import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teachableService } from '../services/teachableService';
import type { TeachableCourse, UnclaimedCourse, TeachableAccount } from '../types/teachable';

export const CoursesPage: React.FC = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['teachableCourses'],
    queryFn: () => teachableService.getCourses(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const claimMutation = useMutation({
    mutationFn: (enrollmentId: number) => teachableService.claimCourse(enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teachableCourses'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });

  const enrolledCourses = data?.enrolled_courses ?? [];
  const unclaimedCourses = data?.unclaimed_courses ?? [];
  const teachableAccount = data?.teachable_account;
  const hasAnyCourses = enrolledCourses.length > 0 || unclaimedCourses.length > 0;

  return (
    <div className="flex-1 min-h-0 bg-dark-bg">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors mb-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-gray-100">My Courses</h1>
          </div>

        {isLoading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-yellow mx-auto" />
            <p className="text-gray-400 mt-4">Loading courses...</p>
          </div>
        )}

        {!isLoading && (
          <>
            {/* Teachable Account Info */}
            {teachableAccount && (
              <TeachableAccountCard account={teachableAccount} />
            )}

            {/* Unclaimed courses */}
            {unclaimedCourses.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-accent-yellow mb-4">
                  Unclaimed Courses
                </h2>
                <div className="space-y-3">
                  {unclaimedCourses.map((course: UnclaimedCourse) => (
                    <UnclaimedCourseCard
                      key={course.id}
                      course={course}
                      onClaim={() => claimMutation.mutate(course.id)}
                      isClaiming={claimMutation.isPending}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Enrolled courses */}
            {enrolledCourses.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-100 mb-4">
                  Enrolled Courses
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {enrolledCourses.map((course: TeachableCourse) => (
                    <EnrolledCourseCard key={course.id} course={course} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state - shown when no courses OR when API failed */}
            {!hasAnyCourses && (
              <div className="text-center py-16">
                <svg
                  className="w-16 h-16 text-gray-600 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-400">No courses yet</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Courses you purchase will appear here.
                </p>
                {error && (
                  <p className="text-xs text-gray-500 mt-3">
                    Having trouble loading? Try refreshing the page.
                  </p>
                )}
              </div>
            )}

            {/* Claim success message */}
            {claimMutation.isSuccess && (
              <div className="bg-green-900/20 border border-bull-blue/30 rounded-lg p-4 mt-4">
                <p className="text-bull-blue">
                  Course claimed successfully! It may take a moment to appear in your enrolled courses.
                </p>
              </div>
            )}

            {/* Claim error message */}
            {claimMutation.isError && (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mt-4">
                <p className="text-red-400">
                  Failed to claim course. Please try again or contact support.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function TeachableAccountCard({ account }: { account: TeachableAccount }): React.ReactElement {
  return (
    <div className="bg-dark-surface border border-dark-border rounded-lg p-4 mb-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Teachable Account
          </h2>
          <div className="space-y-1">
            {account.name && (
              <p className="text-gray-100 text-sm">{account.name}</p>
            )}
            <p className="text-gray-400 text-sm">{account.email}</p>
          </div>
        </div>
        <a
          href="https://courses.steamroomtrading.com"
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 bg-dark-bg hover:bg-dark-border text-gray-300 text-xs font-medium rounded transition-colors"
        >
          Go to Teachable
        </a>
      </div>
    </div>
  );
}

function UnclaimedCourseCard({
  course,
  onClaim,
  isClaiming,
}: {
  course: UnclaimedCourse;
  onClaim: () => void;
  isClaiming: boolean;
}): React.ReactElement {
  const expiresAt = new Date(course.expires_at);
  const daysLeft = Math.max(
    0,
    Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  );

  return (
    <div className="bg-dark-surface border border-accent-yellow/40 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-gray-100 font-medium">
            {course.course_name || 'Unnamed Course'}
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            {daysLeft > 0 ? `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}` : 'Expiring soon'}
          </p>
        </div>
        <button
          onClick={onClaim}
          disabled={isClaiming}
          className="px-4 py-2 bg-accent-yellow hover:bg-yellow-500 text-dark-bg text-sm font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isClaiming ? 'Claiming...' : 'Claim Course'}
        </button>
      </div>
    </div>
  );
}

function EnrolledCourseCard({ course }: { course: TeachableCourse }): React.ReactElement {
  const progressPercent = Math.round(course.percent_complete);

  return (
    <div className="bg-dark-surface border border-dark-border rounded-lg overflow-hidden">
      {course.image_url && (
        <img
          src={course.image_url}
          alt={course.name}
          className="w-full h-36 object-cover"
        />
      )}
      <div className="p-4">
        <h3 className="text-gray-100 font-medium mb-2">{course.name}</h3>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Progress</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="w-full bg-dark-bg rounded-full h-2">
            <div
              className="bg-bull-blue h-2 rounded-full transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {course.course_url && (
          <a
            href={course.course_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-3 py-1.5 bg-dark-bg hover:bg-dark-border text-gray-300 text-xs font-medium rounded transition-colors"
          >
            Go to Course
          </a>
        )}
      </div>
    </div>
  );
}
